import { Link } from "react-router-dom";
import type { ContentTopic } from "@/types/content";
import TopicCard, { formatCount } from "@/components/feature/TopicCard";
import SectionHeading from "@/components/feature/SectionHeading";

export default function TodayTopics({ topics }: { topics: ContentTopic[] }) {
  const [featured, ...rest] = topics;

  if (!featured) {
    return null;
  }

  return (
    <section className="w-full px-4 md:px-8 py-14 md:py-20">
      <SectionHeading
        eyebrow="Today's Melon"
        title="今日热瓜，先看这几件"
        description="编辑挑出当天讨论度最高的话题，附上事件脉络与主要分歧，三分钟就能跟上进度。"
        action={
          <Link
            to="/topics"
            className="inline-flex items-center gap-1.5 text-sm text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap"
          >
            查看完整话题榜
            <i className="ri-arrow-right-line" />
          </Link>
        }
      />

      <div className="mt-8 md:mt-12">
        <article className="reveal group rounded-lg overflow-hidden bg-background-50 border border-background-200/80 hover:border-primary-300 transition-colors duration-300 flex flex-col lg:flex-row">
          <Link
            to={`/topic/${featured.id}`}
            className="relative w-full lg:w-[52%] h-56 sm:h-72 lg:h-auto lg:min-h-[320px] overflow-hidden shrink-0"
            aria-label={featured.title}
          >
            <img
              src={featured.cover}
              alt={`${featured.title} - 今日热门话题配图`}
              title={`${featured.title} - 瓜田吃瓜今日热瓜`}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-background-50/95 text-xs text-primary-700 border border-primary-200">
              编辑部精选
            </span>
          </Link>

          <div className="flex-1 p-5 md:p-8 flex flex-col">
            <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
              <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-800 whitespace-nowrap">
                {featured.category}
              </span>
              <span className="whitespace-nowrap">{featured.publishedAt}</span>
              <span className="whitespace-nowrap">阅读约 {featured.readTime} 分钟</span>
            </div>

            <h3 className="mt-4 text-lg md:text-2xl font-bold text-foreground-950 leading-snug">
              <Link to={`/topic/${featured.id}`} className="hover:text-primary-700 transition-colors">
                {featured.title}
              </Link>
            </h3>

            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              {featured.summary}
            </p>

            <div className="mt-auto pt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <i className="ri-fire-line text-primary-500 w-4 h-4 flex items-center justify-center" />
                热度 {formatCount(featured.heat)}
              </span>
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <i className="ri-chat-3-line w-4 h-4 flex items-center justify-center" />
                {formatCount(featured.comments)} 条讨论
              </span>
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <i className="ri-user-line w-4 h-4 flex items-center justify-center" />
                {featured.author}
              </span>
            </div>
          </div>
        </article>

        <div className="mt-6 md:mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {rest.map((topic) => (
            <TopicCard key={topic.id} topic={topic} />
          ))}
        </div>
      </div>
    </section>
  );
}